import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../api/apiFetch";
import ErrorModal from "../../components/ErrorModal";
import ContractLineForm from "./ContractLineForm";
import ExtendLineModal from "./ExtendLineModal";
import StaffPanel from "./StaffPanel";
import InternalSettings from "./InternalSettings";
import CodeOverview from "./CodeOverview";
import {
  COMPENSATION_LABELS,
  formatDate,
  formatEuro,
  lineStatus,
} from "../../utils/timesheets";

const STATUS_BADGES = {
  active: { label: "Lopend", className: "bg-green-100 text-green-800" },
  upcoming: { label: "Gepland", className: "bg-blue-100 text-blue-800" },
  ended: { label: "Beëindigd", className: "bg-gray-100 text-gray-500" },
};

function describeBreak(line) {
  if (line.compensation_type === "per_item") return "—";
  if (!line.break_prefill) return "Niet voorgevuld";
  return `${line.break_minutes} min vanaf ${Number(line.break_threshold_hours)} u`;
}

// Lijnen met dezelfde persoon, vergoeding, arts en omschrijving vormen één reeks (bv. opeenvolgende tarieven).
function seriesKey(line) {
  return [line.member_id, line.compensation_type, line.reference_member_id || "", (line.description || "").trim().toLowerCase()].join("|");
}

const COLUMN_WIDTHS = ["12%", "10%", "11%", "13%", "7%", "6%", "10%", "7%", "6%", "18%"];

function describePeriod(line) {
  return `${formatDate(line.start_date)} – ${line.end_date ? formatDate(line.end_date) : "onbepaald"}`;
}

export default function ContractLines() {
  const [tab, setTab] = useState("lines");
  const [lines, setLines] = useState([]);
  const [staff, setStaff] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });
  const [notice, setNotice] = useState(null); // { text, warnings: [] }

  const [memberFilter, setMemberFilter] = useState("");
  const [showEnded, setShowEnded] = useState(false);
  const [formState, setFormState] = useState(null); // { line? }
  const [extendLine, setExtendLine] = useState(null);

  const showError = (message) => setErrorModal({ show: true, message });

  const loadAll = useCallback(async () => {
    try {
      const [linesData, staffData, doctorsData, usersData] = await Promise.all([
        apiFetch("/timesheets/contract-lines"),
        apiFetch("/timesheets/staff"),
        apiFetch("/timesheets/references"),
        apiFetch("/timesheets/users"),
      ]);
      setLines(linesData);
      setStaff(staffData);
      setDoctors(doctorsData);
      setUsers(usersData);
    } catch (err) {
      showError(err.message || "Fout bij het ophalen van de gegevens.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const visibleLines = useMemo(
    () =>
      lines.filter(
        (l) =>
          (!memberFilter || l.member_id === memberFilter) && (showEnded || lineStatus(l) !== "ended")
      ),
    [lines, memberFilter, showEnded]
  );

  // Enkel de laatste lijn van een reeks kan verlengd worden met een nieuw tarief.
  const lastInSeries = useMemo(() => {
    const latest = new Map();
    lines.forEach((l) => {
      const key = seriesKey(l);
      if (!latest.has(key) || latest.get(key).start_date < l.start_date) latest.set(key, l);
    });
    return new Set(Array.from(latest.values()).map((l) => l.id));
  }, [lines]);

  const grouped = useMemo(() => {
    const groups = new Map();
    visibleLines.forEach((l) => {
      if (!groups.has(l.member_id)) groups.set(l.member_id, { naam: l.member_naam, lines: [] });
      groups.get(l.member_id).lines.push(l);
    });
    return Array.from(groups.entries());
  }, [visibleLines]);

  const save = async (request, successText) => {
    setSaving(true);
    try {
      const result = await request();
      setNotice({ text: successText, warnings: result.warnings || [] });
      setFormState(null);
      setExtendLine(null);
      await loadAll();
    } catch (err) {
      showError(err.message || "Fout bij het opslaan.");
    } finally {
      setSaving(false);
    }
  };

  const submitForm = (payload) =>
    formState.line
      ? save(
          () => apiFetch(`/timesheets/contract-lines/${formState.line.id}`, { method: "PATCH", body: JSON.stringify(payload) }),
          "Contractlijn gewijzigd."
        )
      : save(
          () => apiFetch("/timesheets/contract-lines", { method: "POST", body: JSON.stringify(payload) }),
          "Contractlijn aangemaakt."
        );

  const submitExtend = (payload) =>
    save(
      () => apiFetch(`/timesheets/contract-lines/${extendLine.id}/extend`, { method: "POST", body: JSON.stringify(payload) }),
      "Nieuwe lijn aangemaakt; de vorige lijn eindigt de dag ervoor."
    );

  const tabClass = (name) =>
    `px-4 py-2 text-sm font-medium border-b-2 ${
      tab === name ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-700"
    }`;

  return (
    <div className="p-4 sm:p-6 max-w-full">
      {errorModal.show && (
        <ErrorModal message={errorModal.message} onClose={() => setErrorModal({ show: false, message: "" })} />
      )}
      {formState && (
        <ContractLineForm
          line={formState.line}
          staff={staff}
          doctors={doctors}
          defaultMemberId={memberFilter}
          saving={saving}
          onSubmit={submitForm}
          onCancel={() => setFormState(null)}
        />
      )}
      {extendLine && (
        <ExtendLineModal line={extendLine} saving={saving} onSubmit={submitExtend} onCancel={() => setExtendLine(null)} />
      )}

      <h1 className="text-2xl font-bold text-gray-800 mb-2">Personeel — contractlijnen</h1>
      <p className="text-sm text-gray-500 mb-4">
        Een contractlijn is een werkovereenkomst met begin- en einddatum en een tarief. Bij elke prestatie kiest het
        personeelslid een lijn; de lijn bepaalt het tarief en de regels.
      </p>

      <div className="flex border-b border-gray-200 mb-4">
        <button className={tabClass("lines")} onClick={() => setTab("lines")}>Contractlijnen</button>
        <button className={tabClass("staff")} onClick={() => setTab("staff")}>Personeel en logins</button>
        <button className={tabClass("internal")} onClick={() => setTab("internal")}>Interne urenstaat</button>
        <button className={tabClass("codes")} onClick={() => setTab("codes")}>Overzicht per code</button>
      </div>

      {notice && (
        <div className="mb-4 rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          <div className="flex justify-between">
            <span>{notice.text}</span>
            <button onClick={() => setNotice(null)} className="text-green-700 hover:text-green-900" aria-label="Sluiten">×</button>
          </div>
          {notice.warnings.length > 0 && (
            <ul className="mt-2 list-disc list-inside text-amber-800">
              {notice.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      ) : tab === "staff" ? (
        <StaffPanel staff={staff} users={users} onChanged={loadAll} onError={showError} />
      ) : tab === "internal" ? (
        <InternalSettings />
      ) : tab === "codes" ? (
        <CodeOverview staff={staff} />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Personeelslid</label>
              <select
                value={memberFilter}
                onChange={(e) => setMemberFilter(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Iedereen</option>
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>{s.naam}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
              <input type="checkbox" checked={showEnded} onChange={(e) => setShowEnded(e.target.checked)} />
              Beëindigde lijnen tonen
            </label>
            <button
              onClick={() => setFormState({})}
              className="ml-auto px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700"
            >
              Nieuwe lijn
            </button>
          </div>

          {grouped.length === 0 ? (
            <div className="text-gray-400 text-sm text-center py-12">
              Geen contractlijnen om te tonen. Maak een nieuwe lijn aan.
            </div>
          ) : (
            grouped.map(([memberId, group]) => (
              <div key={memberId} className="bg-white rounded-lg border border-gray-200 shadow-sm mb-5 overflow-hidden">
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 font-semibold text-gray-700">{group.naam}</div>
                <div className="overflow-x-auto">
                  <table className="w-full table-fixed text-sm" style={{ minWidth: 1280 }}>
                    <colgroup>
                      {COLUMN_WIDTHS.map((width, index) => (
                        <col key={index} style={{ width }} />
                      ))}
                    </colgroup>
                    <thead>
                      <tr className="text-xs text-gray-500 uppercase border-b border-gray-100">
                        <th className="px-4 py-2 text-left">Omschrijving</th>
                        <th className="px-4 py-2 text-left">Vergoeding</th>
                        <th className="px-4 py-2 text-left">Arts</th>
                        <th className="px-4 py-2 text-left">Periode</th>
                        <th className="px-4 py-2 text-right">Tarief</th>
                        <th className="px-4 py-2 text-right">Wijziging</th>
                        <th className="px-4 py-2 text-left">Pauze</th>
                        <th className="px-4 py-2 text-right">Max./maand</th>
                        <th className="px-4 py-2 text-left">Status</th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.lines.map((l) => {
                        const badge = STATUS_BADGES[lineStatus(l)];
                        return (
                          <tr key={l.id} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="px-4 py-2 text-gray-800">{l.description}</td>
                            <td className="px-4 py-2 text-gray-600">{COMPENSATION_LABELS[l.compensation_type]}</td>
                            <td className="px-4 py-2 text-gray-600">{l.reference_naam || "—"}</td>
                            <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{describePeriod(l)}</td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-800">
                              {l.rate === null ? "—" : formatEuro(l.rate)}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                              {l.rate_change_pct === null || l.rate_change_pct === undefined
                                ? ""
                                : `${l.rate_change_pct > 0 ? "+" : ""}${String(l.rate_change_pct).replace(".", ",")}%`}
                            </td>
                            <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{describeBreak(l)}</td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-600">
                              {l.monthly_max_cost === null ? "" : formatEuro(l.monthly_max_cost)}
                            </td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>{badge.label}</span>
                            </td>
                            <td className="px-4 py-2 text-right whitespace-nowrap">
                              <button onClick={() => setFormState({ line: l })} className="text-xs text-blue-600 hover:text-blue-800 mr-3">
                                Bewerken
                              </button>
                              {lineStatus(l) !== "ended" && lastInSeries.has(l.id) && (
                                <button onClick={() => setExtendLine(l)} className="text-xs text-blue-600 hover:text-blue-800">
                                  {l.compensation_type === "salaried" ? "Nieuw rooster" : "Verleng met nieuw tarief"}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
}
